/*
 * The debrief. The leave-behind, read after the room has emptied.
 *
 * Every line here is read by a customer who was not in the room, so it is
 * written for them. The report is derived from the job documents on every
 * render — there is nothing to generate and nothing stored, which is why
 * re-opening a finished session shows the same read.
 */

export const WRAP_COPY = {
  heading: "The Debrief",
  objective: "What the room walks away holding.",
  /** The breadcrumb on the standalone report. */
  crumb: "The Debrief",
  reportTitle: (customerName: string) => `${customerName} Debrief`,
  /** The document's own title, printed at the top of the Markdown export. */
  title: (customerName: string) => `${customerName}: AI opportunity read`,
  meta: (date: string) => `Session board · ${date}`,

  export: "Export",
  exportMarkdown: "Markdown",
  exportMarkdownHint: ".md file for the write-up",
  exportPdf: "PDF · A4",
  exportPdfHint: "Print-ready, one column",

  /** The hero. */
  opportunity: "The opportunity",

  /*
   * Three numbers, and they answer the three questions a customer asks of this
   * sheet in order: how much did we find, how much can we start on, and when
   * does anything land. Every one is derived at render — the shortlist's length,
   * the Must-have count, the first phase's own last week.
   *
   * The old set (to prove / to de-risk / banked) counted the same cards three
   * ways, which read as one fact repeated rather than three answers.
   */
  statOpportunities: (n: number) =>
    n === 1 ? "opportunity identified" : "opportunities identified",
  statBuildReady: "build-ready this pass",
  statFirstValue: "to first value",
  weeks: (n: number) => `${n} wks`,

  /** On the first phase only: the one sentence of advice the sheet gives. */
  startHere: "Start here",

  /** The timeline: one row per kept card, a MoSCoW bar across the weeks it lands in. */
  whatLandsWhen: "What lands when",
  timelineHint: "Prioritised with the room · MoSCoW",
  week: (n: number) => `WK ${n}`,
  weekRange: (from: number, to: number) => (from === to ? `WK ${from}` : `WK ${from}–${to}`),
  /** Could have's own column and badge — see `MOSCOW_COPY.could.when`. */
  later: "Later",

  /** The build grid: Must haves as the lead cards, Should/Could as the follow-on. */
  buildTheseFirst: "Build these first",

  whyItBelongs: "Why it belongs",
  nothingHere: "Nothing landed here.",
  supportingDetail: "Supporting detail",
  supportingDetailBody: "What the room said during the deep dive.",
  alsoRaised: "Also raised",
  alsoRaisedHint: "Discussed in the deep dive, not carried into this pass.",
  jobLabel: (n: number) => `Problem ${n}`,
  thisPass: "This pass",
  noJobs: "The room did not get as far as keeping a card.",
} as const
